/**
 * BuildCompleteCard — Sprint 5 C3 (EFISSUE-812).
 *
 * Renders a `build-complete` tagged Team Log message as a structured card
 * inside the Activity Stream. Collapsed by default — click the header to
 * expand and see the parsed sections.
 *
 * Parsing lives in `src/lib/buildCompleteParser.ts` (pure function, fully
 * tested). This component is presentation only: takes the parsed result
 * (or the raw message for the plain-text fallback) and renders.
 */

import type { TeamLogMessage } from "../types";
import {
  parseBuildComplete,
  extractAllCommitHashes,
  type ParsedBuildSection,
  type SectionVariant,
} from "../lib/buildCompleteParser";
import { authorLabelFor } from "../lib/authorLabel";
import { CollapsibleCard } from "./CollapsibleCard";

/**
 * Commit hash to show in the card header. Single-commit build-completes
 * (kind: "structured") already carry `commitHash`. Session summary posts —
 * multiple tracks/commits rolled into one message — fall back to "plain"
 * parsing since they have no recognised `###` section headers; for those,
 * scan for every distinct commit-like hash in the content and show a count
 * instead of "(no commit hash)" (backlog seq #24552).
 */
function commitLabelFor(content: string, commitHash: string | null): string | null {
  if (commitHash) return commitHash;
  const hashes = extractAllCommitHashes(content);
  if (hashes.length === 0) return null;
  if (hashes.length === 1) return hashes[0];
  return `${hashes.length} commits`;
}

interface Props {
  message: TeamLogMessage;
}

function shortTime(iso: string): string {
  try {
    const d = new Date(iso);
    const now = Date.now();
    const ageMs = now - d.getTime();
    const ageMin = Math.floor(ageMs / 60_000);
    if (ageMin < 1) return "just now";
    if (ageMin < 60) return `${ageMin}m ago`;
    const h = Math.floor(ageMin / 60);
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(h / 24);
    return `${days}d ago`;
  } catch {
    return iso;
  }
}

export function BuildCompleteCard({ message }: Props) {
  const parsed = parseBuildComplete(message.content);

  if (parsed.kind === "plain") {
    // Fallback for messages tagged build-complete but missing recognised
    // section headers. Render in card chrome but show the raw text.
    return (
      <CollapsibleCard
        message={message}
        header={
          <CardHeader
            commitLabel={commitLabelFor(parsed.text, null)}
            sprintLabel={null}
            message={message}
          />
        }
      >
        <div
          style={{
            padding: "10px 14px 14px",
            borderTop: "1px solid var(--border)",
            fontSize: 12,
            color: "var(--text2)",
            whiteSpace: "pre-wrap",
            lineHeight: 1.55,
          }}
        >
          {parsed.text}
        </div>
      </CollapsibleCard>
    );
  }

  return (
    <CollapsibleCard
      message={message}
      header={
        <CardHeader
          commitLabel={commitLabelFor(message.content, parsed.commitHash)}
          sprintLabel={parsed.sprintLabel}
          message={message}
        />
      }
    >
      <div
        style={{
          padding: "10px 14px 6px",
          borderTop: "1px solid var(--border)",
          display: "flex",
          flexDirection: "column",
          gap: 12,
          fontSize: 12,
          color: "var(--text)",
          lineHeight: 1.55,
        }}
      >
        {parsed.sections.map((section, i) => (
          <Section
            key={`${section.variant}-${i}`}
            section={section}
            files={section.variant === "files" ? parsed.files : null}
          />
        ))}
      </div>
    </CollapsibleCard>
  );
}


function CardHeader({
  commitLabel,
  sprintLabel,
  message,
}: {
  commitLabel: string | null;
  sprintLabel: string | null;
  message: TeamLogMessage;
}) {
  const authorLabel = authorLabelFor(message.author);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        flex: 1,
      }}
    >
      <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>
        🔨
      </span>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--text2)",
          }}
        >
          <span style={{ color: "var(--text2)", fontWeight: 500 }}>
            {authorLabel}
          </span>
          <span style={{ color: "var(--text3)" }}>·</span>
          <span style={{ color: "var(--text)" }}>
            {commitLabel ?? "(no commit hash)"}
          </span>
          <span style={{ color: "var(--text3)" }}>→ main</span>
          {message.sequence != null && (
            <span style={{ color: "var(--text3)" }}>· #{message.sequence}</span>
          )}
          {message.tags?.includes("from-modal") && (
            <span
              title="Posted from modal"
              style={{
                fontSize: 11,
                color: "var(--er-accent)",
                background: "var(--surface2)",
                padding: "1px 6px",
                borderRadius: 4,
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                fontFamily: "var(--er-font-mono)",
              }}
            >
              ✓ Modal
            </span>
          )}
        </div>
        {sprintLabel && (
          <div
            style={{
              fontSize: 12,
              color: "var(--text)",
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={sprintLabel}
          >
            {sprintLabel}
          </div>
        )}
      </div>
      <span
        style={{
          fontSize: 10,
          fontFamily: "var(--font-mono)",
          color: "var(--text3)",
          flexShrink: 0,
        }}
      >
        {shortTime(message.timestamp)}
      </span>
    </div>
  );
}

const SECTION_COLOR: Record<SectionVariant, string> = {
  shipped: "var(--text)",
  files: "var(--text2)",
  deferred: "var(--text3)",
  shipConditions: "var(--text2)",
  actionRequired: "var(--er-priority-high)",
  commits: "var(--text2)",
};

function Section({
  section,
  files,
}: {
  section: ParsedBuildSection;
  files: string[] | null;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontFamily: "var(--font-mono)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color:
            section.variant === "actionRequired"
              ? "var(--er-priority-high)"
              : "var(--text3)",
          marginBottom: 4,
          fontWeight: 500,
        }}
      >
        {section.title}
      </div>
      {section.variant === "files" && files && files.length > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {files.map((path) => (
            <span
              key={path}
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "2px 6px",
                borderRadius: 3,
                background: "var(--surface2)",
                border: "1px solid var(--border)",
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                color: "var(--text2)",
              }}
            >
              {path}
            </span>
          ))}
        </div>
      ) : section.variant === "shipConditions" ? (
        <ShipConditionsBody body={section.body} />
      ) : section.variant === "commits" ? (
        <CommitsBody body={section.body} />
      ) : (
        <div
          style={{
            color: SECTION_COLOR[section.variant],
            whiteSpace: "pre-wrap",
            fontSize: 12,
          }}
        >
          {section.body || <em style={{ color: "var(--text3)" }}>(empty)</em>}
        </div>
      )}
    </div>
  );
}

/**
 * Render ship-conditions body as a checklist. Lines starting with ✓ stay
 * green; lines starting with → render in accent; everything else is muted.
 */
function ShipConditionsBody({ body }: { body: string }) {
  const lines = body.split("\n").filter((l) => l.trim().length > 0);
  if (lines.length === 0) {
    return <em style={{ color: "var(--text3)" }}>(empty)</em>;
  }
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 3 }}>
      {lines.map((rawLine, i) => {
        // Strip leading bullet markers so the prefix glyph reads cleanly.
        const line = rawLine.replace(/^\s*[-*+]\s+/, "").trim();
        let prefix: string;
        let color: string;
        let rest: string;
        if (line.startsWith("✓")) {
          prefix = "✓";
          color = "oklch(0.62 0.16 145)"; // green — same as decided dot
          rest = line.slice(1).trim();
        } else if (line.startsWith("→") || line.startsWith("->")) {
          prefix = "→";
          color = "var(--accent)";
          rest = line.replace(/^→|^->/, "").trim();
        } else {
          prefix = "·";
          color = "var(--text2)";
          rest = line;
        }
        return (
          <li key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <span style={{ color, fontFamily: "var(--font-mono)", width: 12, flexShrink: 0, textAlign: "center" }}>
              {prefix}
            </span>
            <span style={{ color: "var(--text)", whiteSpace: "pre-wrap" }}>{rest}</span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Render commits body as compact monospace lines. Strips the surrounding
 * triple-fence if present so the lines render directly.
 */
function CommitsBody({ body }: { body: string }) {
  const fenceMatch = body.match(/```[a-z]*\n?([\s\S]*?)```/);
  const inner = fenceMatch ? fenceMatch[1] : body;
  const lines = inner.split("\n").filter((l) => l.trim().length > 0);
  if (lines.length === 0) {
    return <em style={{ color: "var(--text3)" }}>(empty)</em>;
  }
  return (
    <div
      style={{
        background: "var(--surface2)",
        border: "1px solid var(--border)",
        borderRadius: 4,
        padding: "6px 10px",
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        color: "var(--text2)",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        overflowX: "auto",
      }}
    >
      {lines.map((line, i) => (
        <div key={i} style={{ whiteSpace: "pre" }}>
          {line}
        </div>
      ))}
    </div>
  );
}
